import React, { useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X, ChevronDown, ChevronUp, Music, Sparkles } from "lucide-react";
import { formatTime, pickFakeBitrate, generateFakeFilename } from "@/lib/fakeData";
import Visualizer from "./Visualizer";
import RetroImage from "./RetroImage";

/**
 * Fixed-position, always-visible compact music player at the bottom-right
 * of the viewport. Appears only when a track is loaded. Can be minimized
 * to a tiny pill by the user, or fully hidden (in which case the WinampPlayer
 * shows a "Show Mini" button to restore it).
 */
export default function StickyMiniPlayer({ audio, onSparklesToggle, sparklesOn, hidden, onHide }) {
  const {
    currentTrack, isPlaying, currentTime, duration, volume,
    toggle, prev, next, seekFraction, setVolume, getFrequencyData,
  } = audio;

  const [minimized, setMinimized] = useState(false);

  if (!currentTrack || hidden) return null;

  const bitrate = pickFakeBitrate(currentTrack);
  const trackLine = generateFakeFilename(currentTrack);
  const prettyLine = `${currentTrack.artist} — ${currentTrack.title}`;

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    seekFraction(frac);
  };

  const handleVol = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    setVolume(frac);
  };

  if (minimized) {
    return (
      <button
        data-testid="sticky-mini-restore"
        onClick={() => setMinimized(false)}
        className="fixed bottom-3 left-3 z-40 bevel-out bg-[#0E0E14] flex items-center gap-2 pr-2 py-1 pl-1 hover:bg-[#1A1A24]"
        style={{ minWidth: 220 }}
      >
        <div className="bevel-in w-7 h-7 shrink-0 bg-black overflow-hidden">
          {currentTrack.artwork && <RetroImage src={currentTrack.artwork} alt="" className="w-full h-full object-cover" wrapperClassName="w-full h-full" minDelay={80} />}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div
            className="text-[#FFEB3B] truncate leading-none"
            style={{
              fontFamily: '"Share Tech Mono", monospace',
              textShadow: "0 0 3px #FFEB3B",
              fontSize: 11,
            }}
            title={trackLine}
          >
            {isPlaying ? "▶" : "❚❚"} {trackLine}
          </div>
          <div className="text-[9px] text-[#B6FF00] font-mono leading-none mt-0.5">
            {formatTime(currentTime)} / {formatTime(duration || 30)}
          </div>
        </div>
        <ChevronUp size={14} className="text-[#B6FF00]" />
      </button>
    );
  }

  return (
    <div
      data-testid="sticky-mini-player"
      className="fixed bottom-3 left-3 z-40 bevel-out flex flex-col overflow-hidden"
      style={{ width: 400, background: "#0E0E14", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}
    >
      {/* Title bar */}
      <div className="xp-titlebar-green h-[22px] flex items-center justify-between px-2 select-none">
        <div className="flex items-center gap-1.5 text-[11px] font-bold">
          <Music size={11} />
          Now Playing — LEMONAMP 1.0
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="sticky-sparkles-toggle"
            onClick={onSparklesToggle}
            title={sparklesOn ? "Disable sparkles" : "Enable sparkles"}
            className={`w-[18px] h-[16px] flex items-center justify-center rounded-[2px] text-white ${sparklesOn ? "bg-gradient-to-b from-[#FFEB3B] to-[#FBC02D]" : "bg-gradient-to-b from-[#5A5A6A] to-[#22222E]"}`}
          >
            <Sparkles size={9} className={sparklesOn ? "text-[#3D2E00]" : "text-white"} />
          </button>
          <button
            data-testid="sticky-mini-minimize"
            onClick={() => setMinimized(true)}
            className="w-[18px] h-[16px] flex items-center justify-center rounded-[2px] text-white bg-gradient-to-b from-[#7BB3FF] via-[#247DF6] to-[#003c9e]"
            aria-label="Minimize"
            title="Minimize player"
          >
            <ChevronDown size={10} strokeWidth={3} />
          </button>
          <button
            data-testid="sticky-mini-hide"
            onClick={() => onHide?.()}
            className="w-[18px] h-[16px] flex items-center justify-center rounded-[2px] text-white bg-gradient-to-b from-[#F58D85] via-[#E81123] to-[#8B0A14]"
            aria-label="Close"
            title="Close mini player (re-open from the main player)"
          >
            <X size={10} strokeWidth={3} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-2 flex gap-2">
        {/* Artwork */}
        <div className="bevel-in w-[56px] h-[56px] shrink-0 bg-black overflow-hidden">
          {currentTrack.artwork ? (
            <RetroImage src={currentTrack.artwork} alt="" className="w-full h-full object-cover" wrapperClassName="w-full h-full" minDelay={120} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#B6FF00] text-2xl">♪</div>
          )}
        </div>

        {/* LCD + controls */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="lcd-display lemon px-1.5 py-[2px] min-h-[42px]" style={{ fontFamily: '"Share Tech Mono", monospace' }}>
            <div className="flex items-center justify-between text-[10px] leading-none">
              <span>{isPlaying ? "► PLAYING" : "❚❚ PAUSED"}</span>
              <span>{bitrate} kbps · 44.1kHz</span>
            </div>
            <div className="marquee-outer mt-0.5">
              <div className={trackLine.length > 28 ? "marquee-inner text-[12px]" : "text-[12px]"} title={prettyLine}>
                {trackLine} &nbsp;&nbsp;•&nbsp;&nbsp; {trackLine} &nbsp;&nbsp;•&nbsp;&nbsp;
              </div>
            </div>
            <div className="flex justify-between text-[11px] leading-none mt-0.5">
              <span>{formatTime(currentTime)}</span>
              <span>-{formatTime(Math.max(0, (duration || 30) - currentTime))}</span>
            </div>
          </div>

          {/* Mini visualizer */}
          <div style={{ height: 22 }}>
            <Visualizer getFrequencyData={getFrequencyData} isPlaying={isPlaying} bars={18} height={22} />
          </div>
        </div>
      </div>

      {/* Seek + controls row */}
      <div className="px-2 pb-2 flex flex-col gap-1.5">
        {/* Seek */}
        <div
          data-testid="sticky-seek"
          onClick={handleSeek}
          className="h-2 bg-black border border-[#2A2A3A] cursor-pointer"
        >
          <div
            className="h-full bg-gradient-to-r from-[#1f8020] via-[#B6FF00] to-[#FFEB3B]"
            style={{ width: `${duration ? Math.min(100, (currentTime / duration) * 100) : 0}%` }}
          />
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1">
          <MiniBtn data-testid="sticky-prev" onClick={prev} title="Previous"><SkipBack size={12} /></MiniBtn>
          <MiniBtn data-testid="sticky-toggle" onClick={toggle} title={isPlaying ? "Pause" : "Play"} primary>
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </MiniBtn>
          <MiniBtn data-testid="sticky-next" onClick={next} title="Next"><SkipForward size={12} /></MiniBtn>

          {/* Volume */}
          <div className="flex items-center gap-1 ml-2 flex-1">
            {volume === 0 ? <VolumeX size={12} className="text-[#B6FF00]" /> : <Volume2 size={12} className="text-[#B6FF00]" />}
            <div
              data-testid="sticky-volume"
              onClick={handleVol}
              className="flex-1 h-1.5 bg-black border border-[#2A2A3A] cursor-pointer"
            >
              <div
                className="h-full bg-gradient-to-r from-[#1f8020] to-[#B6FF00]"
                style={{ width: `${Math.round(volume * 100)}%` }}
              />
            </div>
            <span className="text-[9px] font-mono text-[#B6FF00] w-7 text-right">{Math.round(volume * 100)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniBtn({ children, primary, ...props }) {
  return (
    <button
      {...props}
      className={`${primary ? "w-8 h-7" : "w-7 h-6"} flex items-center justify-center rounded-sm border-2 border-t-[#6A6A7A] border-l-[#6A6A7A] border-r-black border-b-black bg-gradient-to-b from-[#4A4A5A] to-[#22222E] text-[#D0D0D0] hover:text-white active:from-[#22222E] active:to-[#4A4A5A] active:border-t-black active:border-l-black active:border-r-[#6A6A7A] active:border-b-[#6A6A7A]`}
    >
      {children}
    </button>
  );
}
